import React from "react";
import { Helmet } from "react-helmet-async";
import { SITE_URL } from "@/lib/api";
import { BRAND } from "@/data/brand";
import { currentLocale, SUPPORTED_LOCALES, DEFAULT_LOCALE } from "@/i18n/locale";

export function Seo({
  title,
  description,
  path = "/",
  image = BRAND.coverWide,
  type = "website",
  jsonLd,
  keywords,
  breadcrumbs,
  faq,
  techArticle,
  howTo,
  dataset,
  persons,
  locale,
  alternates,
}) {
  const cleanPath = path === "/" ? "" : path;
  const urlLocale = currentLocale();
  const activeLocale = locale || urlLocale;
  const fullTitle = title ? `${title} | AstroLife™` : "AstroLife™ | Sovereign AI Infrastructure & Verifiable AI Governance";
  const url = `${SITE_URL}/${urlLocale}${cleanPath}`;
  const ogLocale = { en: "en_GB", es: "es_ES", fr: "fr_FR", de: "de_DE", it: "it_IT" }[activeLocale] || "en_GB";
  const hreflangs = alternates && alternates.length
    ? alternates
    : [
        ...SUPPORTED_LOCALES.map((l) => ({ hreflang: l, href: `${SITE_URL}/${l}${cleanPath}` })),
        { hreflang: "x-default", href: `${SITE_URL}/${DEFAULT_LOCALE}${cleanPath}` },
      ];

  const graphs = [];
  if (jsonLd) graphs.push(jsonLd);
  if (breadcrumbs && breadcrumbs.length) {
    graphs.push({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [{ name: "Home", url: `${SITE_URL}/${urlLocale}` }, ...breadcrumbs].map((b, i) => ({
        "@type": "ListItem",
        position: i + 1,
        name: b.name || b.label,
        item: b.url || (b.to ? `${SITE_URL}/${urlLocale}${b.to}` : url),
      })),
    });
  }
  if (faq && faq.length) {
    graphs.push({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faq.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    });
  }
  if (techArticle) {
    graphs.push({
      "@context": "https://schema.org",
      "@type": "TechArticle",
      headline: techArticle.headline || title,
      description: techArticle.description || description,
      datePublished: techArticle.datePublished,
      dateModified: techArticle.dateModified || techArticle.datePublished,
      proficiencyLevel: techArticle.proficiencyLevel || "Expert",
      mainEntityOfPage: url,
      author: { "@type": "Organization", name: "AstroLife" },
      publisher: { "@type": "Organization", name: "AstroLife" },
      inLanguage: activeLocale,
    });
  }
  if (howTo && howTo.steps && howTo.steps.length) {
    graphs.push({
      "@context": "https://schema.org",
      "@type": "HowTo",
      name: howTo.name || title,
      description: howTo.description || description,
      step: howTo.steps.map((s, i) => ({
        "@type": "HowToStep",
        position: i + 1,
        name: s.name,
        text: s.text,
      })),
    });
  }
  if (persons && persons.length) {
    persons.forEach((p) => {
      graphs.push({
        "@context": "https://schema.org",
        "@type": "Person",
        name: p.name,
        jobTitle: p.jobTitle || p.role,
        worksFor: { "@type": "Organization", name: "AstroLife" },
      });
    });
  }
  if (dataset) {
    graphs.push({
      "@context": "https://schema.org",
      "@type": "Dataset",
      name: dataset.name || title,
      description: dataset.description || description,
      creator: { "@type": "Organization", name: "AstroLife" },
      license: dataset.license,
      keywords: dataset.keywords,
    });
  }

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={url} />
      {hreflangs.map((a) => (
        <link key={a.hreflang} rel="alternate" hrefLang={a.hreflang} href={a.href} />
      ))}

      <meta property="og:type" content={type} />
      <meta property="og:site_name" content="AstroLife" />
      <meta property="og:locale" content={ogLocale} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={image} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {graphs.map((g, i) => (
        <script key={i} type="application/ld+json">{JSON.stringify(g)}</script>
      ))}
    </Helmet>
  );
}

// Site-wide Organization + SoftwareApplication schema (mounted once in Layout).
export function GlobalSchema() {
  const graph = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "@id": `${SITE_URL}/#organization`,
        name: "AstroLife",
        url: SITE_URL,
        description: "Sovereign Artificial Intelligence Infrastructure and Verifiable AI Governance for Europe.",
        areaServed: "European Union",
        sameAs: ["https://governance.astrolife.es", "https://www.linkedin.com/company/astrolife"],
      },
      {
        "@type": "SoftwareApplication",
        "@id": `${SITE_URL}/#governance-os`,
        name: "AstroLife AI Governance OS",
        applicationCategory: "BusinessApplication",
        operatingSystem: "Web, On-Premise, Cloud",
        description: "Enterprise AI Governance Operating System unifying AI inventory, risk, lifecycle, audit readiness and evidence.",
        publisher: { "@id": `${SITE_URL}/#organization` },
        offers: { "@type": "Offer", availability: "https://schema.org/PreOrder" },
      },
    ],
  };
  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(graph)}</script>
    </Helmet>
  );
}
